/**
 * HSE Audit Pack — one-button ZIP of every record on a site.
 *
 * Walks every record type for a date range, fetches the stored PDF where
 * one exists, generates a fresh PDF on the fly where it doesn't, and bundles
 * the lot into a ZIP organised by category folder.
 *
 * Built around graceful failure — if a single record's PDF can't be fetched
 * (stale signed URL, missing object, RLS denial), it's logged and skipped
 * rather than aborting the whole pack.
 */

import JSZip from 'jszip';
import { supabase } from './supabase';
import { getSignedUrl } from './storage';
import {
  getSite, listForms, listAllPermits, listTickRuns, listSiteDocuments,
  listSignInsBetween,
  type Site, type FormRow, type PermitRow, type TickListRun, type DocumentRow,
} from './db';
import { weekOf, nextWeek, buildTimesheet, generateTimesheetPdf } from './timesheet';

export interface AuditPackOpts {
  orgName: string;
  siteId: string;
  fromIso: string;       // inclusive
  toIso: string;         // exclusive
  /** Called with progress updates so the UI can render a counter. */
  onProgress?: (msg: string, done: number, total: number) => void;
}

/** Try to fetch a stored file from a Supabase Storage path. */
async function fetchStored(bucket: string, path: string): Promise<Blob | null> {
  try {
    const url = await getSignedUrl(bucket, path, 600);
    const r = await fetch(url);
    if (!r.ok) return null;
    return await r.blob();
  } catch {
    return null;
  }
}

/** If pdf_url is a relative path, fetch via storage; if it's a full URL, fetch directly. */
async function fetchPdfUrl(pdfUrl: string, fallbackBucket: string): Promise<Blob | null> {
  if (/^https?:\/\//i.test(pdfUrl)) {
    try {
      const r = await fetch(pdfUrl);
      if (!r.ok) return null;
      return await r.blob();
    } catch {
      return null;
    }
  }
  return fetchStored(fallbackBucket, pdfUrl);
}

function safeFile(s: string): string {
  return (s || 'untitled').replace(/[^a-zA-Z0-9_\-. ]+/g, '-').replace(/\s+/g, ' ').trim().slice(0, 80);
}

function fmtDateForFile(iso: string | null | undefined): string {
  if (!iso) return 'undated';
  try { return iso.slice(0, 10); } catch { return 'undated'; }
}

export async function generateAuditPack(opts: AuditPackOpts): Promise<{ blob: Blob; filename: string; counts: Record<string, number> }> {
  const { siteId, fromIso, toIso, onProgress } = opts;
  const zip = new JSZip();
  const counts: Record<string, number> = {
    rams: 0, signins: 0, ticks: 0, forms: 0, permits: 0, documents: 0,
  };

  function progress(msg: string, done: number, total: number) {
    if (onProgress) onProgress(msg, done, total);
  }

  progress('Loading site & records…', 0, 6);
  const [site, forms, ticks, permits, siteDocs, orgDocs] = await Promise.all([
    getSite(siteId),
    listForms(siteId).catch(() => []),
    listTickRuns(siteId).catch(() => []),
    listAllPermits(siteId).catch(() => []),
    listSiteDocuments(siteId).catch(() => []),
    (async () => {
      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .is('site_id', null)
        .eq('is_current', true);
      if (error) return [];
      return (data ?? []) as DocumentRow[];
    })(),
  ]);

  // ---------- Site info summary --------------------------------------------
  if (!site) throw new Error('Site not found.');
  zip.file('00_Site-info.txt', siteInfoText(site, opts.orgName, fromIso, toIso));

  // ---------- Documents (stored PDFs) --------------------------------------
  // Site-scoped first
  const filteredSiteDocs = siteDocs.filter((d) => d.is_current && (!d.created_at || (d.created_at >= fromIso && d.created_at < toIso)));
  let stepDone = 1;
  const totalSteps = 1 + filteredSiteDocs.length + orgDocs.length + permits.length + ticks.length + forms.length + 8;   // approx
  for (const d of filteredSiteDocs) {
    progress(`Documents — ${d.title}`, stepDone++, totalSteps);
    if (!d.file_path) continue;
    const blob = await fetchStored('documents', d.file_path);
    if (blob) {
      const name = safeFile(d.title) + '.pdf';
      zip.file(`06_Documents/site/${name}`, blob);
      counts.documents++;
    }
  }
  for (const d of orgDocs) {
    progress(`Documents — ${d.title}`, stepDone++, totalSteps);
    if (!d.file_path) continue;
    const blob = await fetchStored('documents', d.file_path);
    if (blob) {
      const name = safeFile(d.title) + '.pdf';
      zip.file(`06_Documents/org/${name}`, blob);
      counts.documents++;
    }
  }

  // ---------- Permits ------------------------------------------------------
  const filteredPermits = permits.filter((p) => p.site_id === siteId && p.created_at >= fromIso && p.created_at < toIso);
  for (const p of filteredPermits as PermitRow[]) {
    progress(`Permits — ${p.template_id}`, stepDone++, totalSteps);
    if (!p.file_path) continue;
    const blob = await fetchStored('permits', p.file_path);
    if (blob) {
      const date = fmtDateForFile(p.created_at);
      const name = `${date}-${safeFile(p.template_id ?? 'permit')}.pdf`;
      zip.file(`04_Permits/${name}`, blob);
      counts.permits++;
    }
  }

  // ---------- Tick runs (daily / weekly / monthly) -------------------------
  const filteredTicks = ticks.filter((t) => t.performed_at >= fromIso && t.performed_at < toIso);
  for (const t of filteredTicks as TickListRun[]) {
    progress(`Checks — ${t.kind}`, stepDone++, totalSteps);
    if (!t.pdf_url) continue;
    const blob = await fetchPdfUrl(t.pdf_url, 'tick-list-pdfs');
    if (blob) {
      const date = fmtDateForFile(t.performed_at);
      const name = `${date}-${safeFile(t.kind)}.pdf`;
      zip.file(`03_Daily-and-weekly-checks/${name}`, blob);
      counts.ticks++;
    }
  }

  // ---------- Forms (TBT / accident / subbie induction) --------------------
  const filteredForms = forms.filter((f) => f.created_at >= fromIso && f.created_at < toIso);
  for (const f of filteredForms as FormRow[]) {
    progress(`Forms — ${f.kind}`, stepDone++, totalSteps);
    if (!f.pdf_url) continue;
    const blob = await fetchPdfUrl(f.pdf_url, 'form-pdfs');
    if (blob) {
      const date = fmtDateForFile(f.created_at);
      const folder =
        f.kind === 'accident' ? '05_Accidents-and-near-misses' :
        f.kind === 'toolbox_talk' ? '08_Toolbox-talks' :
        f.kind === 'subcontractor_induction' ? '09_Inductions' :
        '07_Other-forms';
      const name = `${date}-${safeFile(f.title || f.kind)}.pdf`;
      zip.file(`${folder}/${name}`, blob);
      counts.forms++;
    }
  }

  // ---------- Sign-in / hours — per-week timesheet PDFs --------------------
  // Walk weeks in the range, generate a timesheet PDF for each that has any sign-ins.
  let week = weekOf(new Date(fromIso));
  while (week.startIso < toIso) {
    progress(`Sign-in log — week of ${week.label}`, stepDone++, totalSteps);
    const weekIns = await listSignInsBetween(siteId, week.startIso, week.endIso).catch(() => []);
    if (weekIns.length > 0) {
      const grid = buildTimesheet(week, weekIns);
      const blob = generateTimesheetPdf({
        orgName: opts.orgName,
        siteName: site.name,
        week,
        grid,
      });
      const name = `Week-of-${week.startDate.toISOString().slice(0, 10)}.pdf`;
      zip.file(`02_Sign-in-logs/${name}`, blob);
      counts.signins++;
    }
    week = nextWeek(week);
    // Safety stop in case range is huge
    if (counts.signins > 200) break;
  }

  progress('Compressing ZIP…', totalSteps, totalSteps);
  const blob = await zip.generateAsync({ type: 'blob' });

  const safeName = safeFile(site.name).replace(/\s+/g, '-');
  const dateStr = new Date().toISOString().slice(0, 10);
  const filename = `HSE-pack-${safeName}-${dateStr}.zip`;

  return { blob, filename, counts };
}

function siteInfoText(site: Site, orgName: string, fromIso: string, toIso: string): string {
  const lines = [
    'HSE AUDIT PACK',
    '',
    `Generated: ${new Date().toLocaleString('en-GB')}`,
    `Organisation: ${orgName}`,
    `Site: ${site.name}`,
    site.address ? `Address: ${site.address}` : '',
    site.project_type ? `Project type: ${site.project_type.replace(/_/g, ' ')}` : '',
    site.in_charge != null ? `In charge: ${site.in_charge ? 'Yes — Principal Contractor on this site' : 'No — working under another main contractor'}` : '',
    site.geofence_radius_m ? `Geofence: ${site.geofence_radius_m}m radius` : '',
    '',
    `Date range covered:`,
    `  From: ${fromIso.slice(0, 10)}`,
    `  To:   ${toIso.slice(0, 10)}`,
    '',
    'Folder guide:',
    '  01_Site-info.txt              You are reading it',
    '  02_Sign-in-logs/              Weekly hours / who was on site',
    '  03_Daily-and-weekly-checks/   Tick lists',
    '  04_Permits/                   Hot works, dig, height, confined',
    '  05_Accidents-and-near-misses/',
    '  06_Documents/                 CPP, F10, PCI, H&S Policy, etc.',
    '  07_Other-forms/',
    '  08_Toolbox-talks/',
    '  09_Inductions/',
    '',
    'NB. RAMS for this site are stored as documents — see folder 06_Documents.',
    '    Items missing from this pack typically had no stored PDF — open the',
    '    record in the app and re-export it if needed.',
  ];
  return lines.filter((l) => l !== null).join('\n');
}
